import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import {
  CompanyType,
  JobRole,
  RoleConfig,
  CompanyConfig,
  COMPANY_ROLE_CONFIGS,
  COMPANY_CONFIGS,
  ALL_NAV_ITEMS,
} from '../mocks/companyTypes';

// re-export for backward compat
export type { CompanyType, JobRole, RoleConfig, CompanyConfig };
export { COMPANY_ROLE_CONFIGS, COMPANY_CONFIGS, ALL_NAV_ITEMS };

// ─── 알림 타입 ────────────────────────────────────────────────────
export type RoleNotification = {
  id: string;
  from_user_id: string;
  to_role: string;
  type: string;
  title: string;
  body: string;
  ref_id: string | null;
  is_read: boolean;
  created_at: string;
};

// ─── Context 타입 ─────────────────────────────────────────────────
type RoleContextType = {
  // 기업 유형
  companyType: CompanyType;
  companyConfig: CompanyConfig;
  setCompanyType: (type: CompanyType) => Promise<void>;
  // 직무
  jobRole: JobRole;
  roleConfig: RoleConfig;
  roleConfigs: RoleConfig[];
  setJobRole: (role: JobRole) => Promise<void>;
  isRoleLocked: boolean; // student + profile.job_role already set → locked
  // 알림
  notifications: RoleNotification[];
  unreadCount: number;
  markAllRead: () => Promise<void>;
  sendNotification: (toRole: string, type: string, title: string, body: string, refId?: string) => Promise<void>;
};

const RoleContext = createContext<RoleContextType | null>(null);

const COMPANY_KEY = 'kap_company_type';
const ROLE_KEY    = 'kap_job_role';

// ─── Provider ─────────────────────────────────────────────────────
export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();

  const storedCompany = (localStorage.getItem(COMPANY_KEY) ?? 'MusicLabel') as CompanyType;
  const storedRole    = (localStorage.getItem(ROLE_KEY) ?? 'CEO') as JobRole;

  const [companyType, setCompanyTypeState] = useState<CompanyType>(storedCompany);
  const [jobRole, setJobRoleState]         = useState<JobRole>(storedRole);
  const [notifications, setNotifications]  = useState<RoleNotification[]>([]);
  const [dbRoleLocked, setDbRoleLocked]    = useState(false);

  const companyConfig = COMPANY_CONFIGS[companyType];
  const roleConfigs   = COMPANY_ROLE_CONFIGS[companyType];
  const roleConfig    = roleConfigs.find(r => r.key === jobRole) ?? roleConfigs[0];

  // DB role_locked 또는 학생+profile.job_role 설정됨 → 잠금
  const isRoleLocked = profile?.role === 'student' && (dbRoleLocked || !!profile?.job_role);

  // workspace_members에서 role_locked + 강제 지정 직무 동기화
  useEffect(() => {
    if (!user || profile?.role !== 'student') return;
    const wsId = localStorage.getItem('kap_solo_workspace_id');
    if (!wsId) return;
    supabase
      .from('workspace_members')
      .select('job_role, role_locked')
      .eq('workspace_id', wsId)
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        if (data.role_locked) setDbRoleLocked(true);
        // 어드민이 강제 지정한 직무가 있으면 덮어씀
        if (data.job_role && data.job_role !== storedRole) {
          setJobRoleState(data.job_role as JobRole);
          localStorage.setItem(ROLE_KEY, data.job_role);
        }
      });
  }, [user, profile?.role]);

  const unreadCount = notifications.filter(n => !n.is_read && n.to_role === jobRole).length;

  // ─── 기업 유형 변경 ──────────────────────────────────────────
  const setCompanyType = useCallback(async (type: CompanyType) => {
    setCompanyTypeState(type);
    localStorage.setItem(COMPANY_KEY, type);
    // 기업 유형 변경 시 직무도 해당 기업의 CEO로 초기화
    const newRoles = COMPANY_ROLE_CONFIGS[type];
    const ceoRole = newRoles.find(r => r.key === 'CEO') ?? newRoles[0];
    setJobRoleState(ceoRole.key);
    localStorage.setItem(ROLE_KEY, ceoRole.key);
    if (user) {
      const wsId = localStorage.getItem('kap_solo_workspace_id');
      if (wsId) {
        await supabase.from('workspaces').update({ company_type: type }).eq('id', wsId);
      }
      await supabase.from('profiles').update({ job_role: ceoRole.key }).eq('id', user.id);
    }
  }, [user]);

  // ─── 직무 변경 ───────────────────────────────────────────────
  const setJobRole = useCallback(async (role: JobRole) => {
    // 학생 + 이미 직무 설정됨 → 잠금 (관리자만 변경 가능)
    if (isRoleLocked) return;
    setJobRoleState(role);
    localStorage.setItem(ROLE_KEY, role);
    if (user) {
      // 1. profiles 업데이트
      await supabase.from('profiles').update({ job_role: role }).eq('id', user.id);
      // 2. workspace_members.job_role 동기화 (수업 워크스페이스 참여 시)
      const wsId = localStorage.getItem('kap_solo_workspace_id');
      if (wsId) {
        await supabase
          .from('workspace_members')
          .update({ job_role: role })
          .eq('workspace_id', wsId)
          .eq('user_id', user.id);
      }
    }
  }, [user, isRoleLocked]);

  // ─── 알림 가져오기 ───────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    const wsId = localStorage.getItem('kap_solo_workspace_id');
    if (!wsId) return;
    const { data } = await supabase
      .from('role_notifications')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: false })
      .limit(30);
    if (data) setNotifications(data as RoleNotification[]);
  }, [user]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markAllRead = useCallback(async () => {
    const wsId = localStorage.getItem('kap_solo_workspace_id');
    if (!wsId || !user) return;
    await supabase.from('role_notifications')
      .update({ is_read: true })
      .eq('workspace_id', wsId)
      .eq('to_role', jobRole);
    setNotifications(prev => prev.map(n => n.to_role === jobRole ? { ...n, is_read: true } : n));
  }, [jobRole, user]);

  const sendNotification = useCallback(async (toRole: string, type: string, title: string, body: string, refId?: string) => {
    if (!user) return;
    const wsId = localStorage.getItem('kap_solo_workspace_id');
    if (!wsId) return;
    const { data } = await supabase.from('role_notifications').insert({
      workspace_id: wsId,
      from_user_id: user.id,
      to_role: toRole,
      type,
      title,
      body,
      ref_id: refId ?? null,
    }).select().maybeSingle();
    if (data) setNotifications(prev => [data as RoleNotification, ...prev]);
  }, [user]);

  return (
    <RoleContext.Provider value={{
      companyType, companyConfig, setCompanyType,
      jobRole, roleConfig, roleConfigs, setJobRole, isRoleLocked,
      notifications, unreadCount, markAllRead, sendNotification,
    }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}

// ─── 레거시 호환 export (Sidebar 등에서 쓰던 ROLE_CONFIGS) ─────────
// 사용처에서 useRole().roleConfigs로 교체 권장
export const ROLE_CONFIGS = COMPANY_ROLE_CONFIGS['MusicLabel'];
